import React, { useEffect, useState } from 'react';
import { Alert, Form, Input, Modal, Select, Space, Typography, message } from 'antd';

const { Text } = Typography;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface SendReportFormValues {
  to: string[];
  cc: string[];
  subject: string;
  message: string;
}

interface Props {
  open: boolean;
  title: string;
  okText?: string;
  defaults: Partial<SendReportFormValues>;
  attachmentLabel: string;
  /** Shown under the To field, e.g. "No email on the customer master". */
  recipientHint?: string;
  onPreview?: () => void;
  onSubmit: (values: SendReportFormValues) => Promise<{ recipients: string[] }>;
  onClose: () => void;
  onSent?: () => void;
}

const emailRule = {
  validator: (_: unknown, value: string[] | undefined) => {
    const bad = (value ?? []).filter((e) => !EMAIL_RE.test(e.trim()));
    return bad.length ? Promise.reject(new Error(`Invalid email: ${bad.join(', ')}`)) : Promise.resolve();
  },
};

const SendReportModal: React.FC<Props> = ({
  open,
  title,
  okText = 'Send',
  defaults,
  attachmentLabel,
  recipientHint,
  onPreview,
  onSubmit,
  onClose,
  onSent,
}) => {
  const [form] = Form.useForm<SendReportFormValues>();
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({ to: [], cc: [], subject: '', message: '', ...defaults });
  }, [open, defaults, form]);

  const submit = async () => {
    const values = await form.validateFields();
    setSending(true);
    try {
      const res = await onSubmit({ ...values, cc: values.cc ?? [] });
      message.success(`Emailed to ${res.recipients.join(', ')}`);
      onSent?.();
      onClose();
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Could not send the email');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open={open} title={title} okText={okText} onOk={submit} confirmLoading={sending} onCancel={onClose} width={640} destroyOnClose>
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Alert
          type="info"
          showIcon
          message={
            <span>
              {attachmentLabel}
              {onPreview && (
                <>
                  {' '}
                  <a onClick={onPreview}>Preview PDF</a>
                </>
              )}
            </span>
          }
        />
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            name="to"
            label="To"
            rules={[{ required: true, message: 'Add at least one recipient' }, emailRule]}
            extra={recipientHint ? <Text type="warning">{recipientHint}</Text> : undefined}
          >
            <Select mode="tags" tokenSeparators={[',', ';', ' ']} placeholder="name@example.com" open={false} />
          </Form.Item>
          <Form.Item name="cc" label="CC" rules={[emailRule]}>
            <Select mode="tags" tokenSeparators={[',', ';', ' ']} placeholder="accounts@yourcompany.com" open={false} />
          </Form.Item>
          <Form.Item name="subject" label="Subject" rules={[{ required: true, max: 500 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="message" label="Message" rules={[{ max: 4000 }]}>
            <Input.TextArea rows={6} />
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  );
};

export default SendReportModal;
